import React, { useState, useEffect, useCallback } from 'react'
import EmptyTable from './EmptyTable'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import { countryNames } from './config/countryNames'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ProxyDetailsDialog from './ProxyDetailsDialog'
import useMobile from './hooks/useMobile'
import MobileMenu from './components/MobileMenu'
import AnimatedBurgerIcon from './components/AnimatedBurgerIcon'
import MobileActiveProxiesTable from './components/MobileActiveProxiesTable'
import NoteEditor from './components/NoteEditor'
import PaginationActions from './components/PaginationActions'

// Компонент Tooltip
const Tooltip = ({ title, children, placement = "top" }) => {
    const [show, setShow] = useState(false)
    
    return (
        <div 
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && (
                <div style={{
                    position: 'absolute',
                    bottom: placement === 'top' ? '100%' : 'auto',
                    top: placement === 'bottom' ? '100%' : 'auto',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--dark-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    boxShadow: 'var(--shadow-card)',
                    border: '1px solid var(--light-gray)',
                    marginBottom: placement === 'top' ? '8px' : '0',
                    marginTop: placement === 'bottom' ? '8px' : '0'
                }}>
                    {title}
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        ...(placement === 'top' ? {
                            top: '100%',
                            borderTop: '5px solid var(--light-gray)'
                        } : {
                            bottom: '100%',
                            borderBottom: '5px solid var(--light-gray)'
                        })
                    }} />
                </div>
            )}
        </div>
    )
}

const ActiveProxiesTable = (props) => {
    const { isMobile, isTablet } = useMobile()
    const { getAccessTokenSilently } = useAuth()
    const [selectedRow, setSelectedRow] = useState([])
    const [isRefund, setIsRefund] = useState(false)
    const activeProxiesList = props.list?.rentals || []
    const [openDetails, setOpenDetails] = React.useState(false)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    
    // Состояние для пагинации
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [totalProxies, setTotalProxies] = React.useState(0)
    
    // Состояние для заметок
    const [editingNoteFor, setEditingNoteFor] = useState(null)
    const [notes, setNotes] = useState({})

    // Локальное состояние автопродления (rentalid -> bool) и индикатор загрузки
    const [autoRenewMap, setAutoRenewMap] = useState({})
    const [autoRenewLoading, setAutoRenewLoading] = useState({})

    const getAutoRenew = useCallback((row) => {
        return autoRenewMap[row?.rentalid] !== undefined
            ? autoRenewMap[row?.rentalid]
            : !!row?.autoRenew
    }, [autoRenewMap])

    const handleToggleAutoRenew = useCallback(async (row) => {
        const id = row?.rentalid
        if (!id || autoRenewLoading[id]) return

        const next = !getAutoRenew(row)
        setAutoRenewLoading(prev => ({ ...prev, [id]: true }))
        try {
            await props.apiClient.setToken(getAccessTokenSilently()).setAutoRenew(id, next)
            setAutoRenewMap(prev => ({ ...prev, [id]: next }))
        } catch (e) {
            // оставляем прежнее состояние при ошибке
        } finally {
            setAutoRenewLoading(prev => ({ ...prev, [id]: false }))
        }
    }, [autoRenewLoading, getAutoRenew, props, getAccessTokenSilently])
    

    // CSS стили для кнопки и полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            .visibility-button:hover {
                background-color: rgba(255, 152, 0, 0.1) !important;
            }
            .visibility-button:not(:hover) {
                background-color: transparent !important;
            }
            
            /* Адаптивная полоса прокрутки для таблицы ActiveProxies */
            .active-proxies-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .active-proxies-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .active-proxies-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .active-proxies-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .active-proxies-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .active-proxies-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .active-proxies-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .active-proxies-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .active-proxies-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .active-proxies-table-container,
            :root:not([data-theme="light"]) .active-proxies-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // console.log(activeProxiesList)

    const formatTimeLeft = (expiresAt) => {
        const diffMs = new Date(expiresAt).getTime() - Date.now()
        const minutes = Math.max(0, Math.floor(diffMs / (60 * 1000) % 60))
        const hours = Math.floor(diffMs / (60 * 60 * 1000))
    
        let result = minutes + "m"
        if (hours > 0) {
            result = hours + "h " + result
        }
    
        if (result === "0m") {
            result = "now"
        }
    
        return result
    }

    // Убираем автоматический выбор первой прокси
    // useEffect(()=>{
    //     if(activeProxiesList.length > 0 && selectedRow.length === 0){
    //         setSelectedRow(activeProxiesList[0])
    //     }}
    // )

    // Обработчики для пагинации
    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    // Обновляем общее количество прокси при изменении списка
    React.useEffect(() => {
        setTotalProxies(activeProxiesList.length)
    }, [activeProxiesList])

    // Обновляем данные при каждом открытии вкладки
    React.useEffect(() => {
        const refreshData = async () => {
            try {
                if (props.setReloadActive) {
                    props.setReloadActive(true)
                }
            } catch (error) {
                console.error('Error refreshing active proxies:', error)
            }
        }
        
        refreshData()
    }, []) // Пустой массив зависимостей = выполняется только при монтировании

    // Обновляем данные при фокусе на вкладке
    React.useEffect(() => {
        const handleFocus = () => {
            if (props.setReloadActive) {
                props.setReloadActive(true)
            }
        }

        // Добавляем обработчик для события focus
        window.addEventListener('focus', handleFocus)
        
        // Очищаем обработчик при размонтировании
        return () => {
            window.removeEventListener('focus', handleFocus)
        }
    }, [props.setReloadActive])

    // Получаем данные для текущей страницы с дефолтной сортировкой
    const getCurrentPageData = () => {
        // Сортируем по времени истечения (больше времени сверху)
        const sortedList = [...activeProxiesList].sort((a, b) => {
            const timeA = a.expiresAt ? new Date(a.expiresAt).getTime() : 0
            const timeB = b.expiresAt ? new Date(b.expiresAt).getTime() : 0
            return timeB - timeA // desc = больше времени сверху
        })
        
        const startIndex = page * rowsPerPage
        const endIndex = startIndex + rowsPerPage
        return sortedList.slice(startIndex, endIndex)
    }

    // Возраст прокси из listedDays (кол-во дней в листинге)
    const calculateProxyAge = (listedDays) => {
        if (listedDays == null) return 'N/A'

        if (listedDays >= 365) return `${Math.floor(listedDays / 365)}y`
        if (listedDays >= 30) return `${Math.floor(listedDays / 30)}m`
        return `${listedDays}d`
    }

    
    const rowClick = (row) => {
        setSelectedRow(row)
    }
    
    // const handleClick = (event, row) => {
        // console.log(row)
        // const diffMs = row?.until - Date.now()
        // const REFUND_MAX_HOURS = 3
        // const hoursUsed = 24 - Math.floor(diffMs / (60 * 60 * 1000))

        // if (hoursUsed < REFUND_MAX_HOURS) {
        //     setIsRefund(true)
        // }
        // console.log(diffMs)
        // setAnchorEl(event.currentTarget)
        // setSelectedRow(row)
    // }

    const handleClick = (row, event) => {
        setSelectedRow(row)
        setOpenDetails(true)
    }
    
    
    const handleClose = (key, row) => {
        // console.log(row)
        setSelectedRow(row)
    }

    // Обработчики для заметок
    const handleEditNote = (agentId) => {
        setEditingNoteFor(agentId)
    }

    const handleSaveNote = (agentId, note) => {
        setNotes(prev => ({ ...prev, [agentId]: note }))
        setEditingNoteFor(null)
    }

    const handleCancelNote = () => {
        setEditingNoteFor(null)
    }


    return (
        <>
        {activeProxiesList.length > 0 ? (
            <div className="shop-page" style={{ 
                width: isMobile ? '100%' : 'calc(100% - 280px)',
                height: '100vh',
                padding: isMobile ? '0' : '8px',
                marginLeft: isMobile ? '0' : '280px',
                overflow: isMobile ? 'hidden' : 'auto'
            }}>
                {/* Мобильная навигация */}
                {isMobile && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-md)',
                        background: 'var(--dark-gray)',
                        borderBottom: '1px solid var(--light-gray)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100
                    }}>
                        <AnimatedBurgerIcon
                            isOpen={mobileMenuOpen}
                            onClick={() => setMobileMenuOpen(true)}
                        />
                        <h1 className="shop-page-title" style={{ 
                            margin: 0,
                            fontSize: 'var(--text-lg)',
                        }}>
                            Active Proxies
                        </h1>
                        <div style={{ width: '32px' }} /> {/* Spacer for centering */}
                    </div>
                )}

                {/* Мобильное меню */}
                {isMobile && (
                    <MobileMenu 
                        user={props.user} 
                        active={props.active} 
                        apiClient={props.apiClient}
                        isOpen={mobileMenuOpen}
                        onClose={() => setMobileMenuOpen(false)}
                    />
                )}

                {/* Контент */}
                <div style={{
                    height: isMobile ? 'calc(100vh - 60px)' : 'auto',
                    overflow: isMobile ? 'auto' : 'visible',
                    padding: isMobile ? 'var(--spacing-sm)' : '0'
                }}>
                    {!isMobile && (
                        <h1 className="shop-page-title" style={{ 
                            marginTop: '8px',
                            marginBottom: '8px',
                            marginLeft: '0px',
                            fontSize: 'var(--text-2xl)',
                        }}>
                            Active Proxies
                        </h1>
                    )}
                
                {/* Мобильная версия таблицы */}
                {isMobile && (
                    <MobileActiveProxiesTable
                        activeProxiesList={activeProxiesList}
                        selectedRow={selectedRow}
                        onRowClick={rowClick}
                        onDetailsClick={handleClick}
                        apiClient={props.apiClient}
                    />
                )}
                
                {/* Десктопная версия таблицы */}
                {!isMobile && (
                <>
                <div className="table-container active-proxies-table-container" style={{ 
                    marginTop: 'var(--spacing-sm)', 
                    marginLeft: '0px',
                    overflowX: 'auto'
                }}>
                    <table className="table table-proxies" style={{ minWidth: '1000px' }}>
                        <thead>
                            <tr>
                                <th>IP</th>
                                <th>Country</th>
                                <th>State</th>
                                <th>City</th>
                                <th>Zip</th>
                                <th style={{ width: '200px' }}>ISP</th>
                                <th style={{ textAlign: 'center' }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center'
                                    }}>
                                        <span style={{ position: 'relative', display: 'inline-block' }}>
                                            <span 
                                                style={{
                                                    cursor: 'help'
                                                }}
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'block';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'none';
                                                }}
                                            >
                                                Age
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'var(--medium-gray)',
                                                color: 'var(--text-light)',
                                                padding: '8px 10px',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--text-xs)',
                                                whiteSpace: 'nowrap',
                                                zIndex: 10000,
                                                border: '1px solid var(--light-gray)',
                                                boxShadow: 'var(--shadow-md)',
                                                marginTop: '5px',
                                                display: 'none',
                                                maxWidth: '200px',
                                                whiteSpace: 'normal',
                                                textAlign: 'center',
                                                pointerEvents: 'none'
                                            }}>
                                                Proxy age
                                            </div>
                                        </span>
                                        /
                                        <span style={{ position: 'relative', display: 'inline-block' }}>
                                            <span 
                                                style={{
                                                    cursor: 'help'
                                                }}
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'block';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tooltip = e.target.nextElementSibling;
                                                    if (tooltip) tooltip.style.display = 'none';
                                                }}
                                            >
                                                Activity
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'var(--medium-gray)',
                                                color: 'var(--text-light)',
                                                padding: '8px 10px',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--text-xs)',
                                                whiteSpace: 'nowrap',
                                                zIndex: 10000,
                                                border: '1px solid var(--light-gray)',
                                                boxShadow: 'var(--shadow-md)',
                                                marginTop: '5px',
                                                display: 'none',
                                                maxWidth: '200px',
                                                whiteSpace: 'normal',
                                                textAlign: 'center',
                                                pointerEvents: 'none'
                                            }}>
                                                Average proxy online time during the day
                                            </div>
                                        </span>
                                    </div>
                                </th>
                                <th style={{ textAlign: 'center' }}>Users</th>
                                <th>Expire at</th>
                                <th>INFO</th>
                                <th>Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getCurrentPageData().map((row, index) => (
                                <tr 
                                    key={row?.rentalid || index}
                                    onClick={() => rowClick(row)}
                                    style={{
                                        backgroundColor: selectedRow?.rentalid === row?.rentalid ? 'var(--shop-row-selected-bg)' : 'transparent',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <td>
                                        <Tooltip title={'Proxy ID: ' + row?.rentalid} placement="top">
                                            <span>{row?.proxyinfo?.ip}</span>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <ReactCountryFlag 
                                                countryCode={row?.proxyinfo?.geo?.cc} 
                                                svg 
                                                style={{width: '16px', height: '12px'}}
                                            />
                                            {row?.proxyinfo?.geo?.cc}
                                        </div>
                                    </td>
                                    <td>{row?.proxyinfo?.geo?.reg?.replace(/(.{10})..+/, "$1…")}</td>
                                    <td>{row?.proxyinfo?.geo?.city?.replace(/(.{10})..+/, "$1…")}</td>
                                    <td>{row?.proxyinfo?.geo?.zip || ''}</td>
                                    <td style={{ width: '200px' }}>{row?.proxyinfo?.geo?.isp}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {row?.proxyinfo?.uptime !== undefined ? (
                                            <span className="shop-accent-text">
                                                {calculateProxyAge(row?.proxyinfo?.listedDays)}/{Math.round(row?.proxyinfo?.uptime || 0)}%
                                            </span>
                                        ):(
                                            <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>{row?.proxyinfo?.users}</td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', lineHeight: 1 }}>
                                            <Tooltip title={`AutoRenew: ${getAutoRenew(row) ? 'On' : 'Off'}`} placement="top">
                                                <AutorenewIcon
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleToggleAutoRenew(row)
                                                    }}
                                                    style={{
                                                        display: 'block',
                                                        fontSize: 18,
                                                        cursor: autoRenewLoading[row?.rentalid] ? 'wait' : 'pointer',
                                                        fill: getAutoRenew(row) ? 'url(#goldGradient)' : 'var(--text-muted)',
                                                        opacity: autoRenewLoading[row?.rentalid] ? 0.5 : 1,
                                                        transition: 'var(--transition-normal)'
                                                    }}
                                                />
                                            </Tooltip>
                                            <span style={{ lineHeight: 1 }}>{formatTimeLeft(row?.expiresAt)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleClick(row, e)
                                            }}
                                            className="visibility-button"
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                borderRadius: 'var(--radius-sm)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'background-color 0.2s ease'
                                            }}
                                        >
                                            <MoreHorizIcon className="table-proxies-icon shop-active-info-icon" style={{ fontSize: '32px' }} />
                                        </button>
                                    </td>
                                    <td style={{ width: '200px', maxWidth: '200px', wordWrap: 'break-word' }}>
                                        {editingNoteFor === row?.proxyid ? (
                                            <NoteEditor
                                                proxy={row}
                                                apiClient={props.apiClient}
                                                currentNote={notes[row?.proxyid] ?? row?.note}
                                                onSave={(note) => handleSaveNote(row?.proxyid, note)}
                                                onCancel={handleCancelNote}
                                            />
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleEditNote(row?.proxyid)
                                                    }}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'var(--primary-orange)',
                                                        cursor: 'pointer',
                                                        fontSize: 'var(--text-sm)',
                                                        padding: '2px 4px',
                                                        borderRadius: 'var(--radius-sm)',
                                                        transition: 'var(--transition-normal)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'var(--dark-gray)'
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'transparent'
                                                    }}
                                                >
                                                    <EditNoteIcon className="table-proxies-icon shop-active-info-icon" style={{ fontSize: 20 }} />
                                                </button>
                                                <span style={{
                                                    fontSize: 'var(--text-sm)',
                                                    color: notes[row?.proxyid] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                                                    fontStyle: notes[row?.proxyid] || row?.note ? 'normal' : 'italic'
                                                }}>
                                                    {(notes[row?.proxyid] || row?.note || 'No note').length > 10 
                                                        ? (notes[row?.proxyid] || row?.note || 'No note').substring(0, 10) + '...'
                                                        : (notes[row?.proxyid] || row?.note || 'No note')
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="shop-table-footer" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-lg)',
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-md)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '8px 12px',
                                    fontSize: 'var(--text-sm)'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        <PaginationActions 
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={totalProxies}
                            onPageChange={handleChangePage}
                            centerLabel="range"
                        />
                    </div>
                </div>
                </>
                )}
                
                <ProxyDetailsDialog 
                    open={openDetails} 
                    selectedProxy={selectedRow} 
                    setOpenDetails={setOpenDetails} 
                    setReloadActive={props.setReloadActive}
                    refreshUserData={props.refreshUserData}
                    apiClient={props.apiClient}
                />
                </div>
            </div>
            ):(
                <EmptyTable name="Active Proxies" text="You don't have any active proxies."/>
            )
        }
        </>
    )
}

export default ActiveProxiesTable